import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import Pagination from '../../components/common/Pagination';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Modals.css';

const AssetHistoryReportPage = () => {
  const [loading, setLoading] = useState(true);
  const [handovers, setHandovers] = useState([]);
  const [checkinHistory, setCheckinHistory] = useState({}); // Map of assetId -> checkin date
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [emailFormat, setEmailFormat] = useState('pdf');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'employee', 'asset'
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState({ checkoutPhotos: [], checkinPhotos: [] });
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    employee: '',
    asset: '',
    showCheckout: false,
  });

  // Fetch employees for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.getUsers({ role: 'employee', limit: 1000 });
        if (response.success) {
          setEmployees(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch assets for filter dropdown
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await api.getAssets({ limit: 1000 });
        if (response.success) {
          setAssets(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching assets:', error);
      }
    };
    fetchAssets();
  }, []);

  // Fetch admins for email dropdown
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await api.getUsers({ role: 'admin', limit: 1000 });
        if (response.success) {
          const adminList = response.data || [];
          setAdmins(adminList);
          // Select all admins by default
          setSelectedAdmins(adminList.map(admin => admin.id || admin._id));
        }
      } catch (error) {
        console.error('Error fetching admins:', error);
      }
    };
    fetchAdmins();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Build query parameters - all filtering handled by backend
        const params = {
          page: currentPage,
          limit: itemsPerPage
        };

        // Add date filters
        if (filters.startDate) {
          params.dateFrom = filters.startDate;
        }
        if (filters.endDate) {
          params.dateTo = filters.endDate;
        }

        // Add employee filter
        if (filters.employee) {
          params.assignedTo = filters.employee;
        }

        // Add asset filter
        if (filters.asset) {
          params.asset = filters.asset;
        }

        // Handle checkout filter - show only pending (not checked in) handovers
        if (filters.showCheckout) {
          params.acknowledgementStatus = 'pending';
        }

        // Fetch handovers with pagination and filters from backend
        const res = await api.getHandovers(params);

        if (res.success) {
          const handoverData = res.data || [];

          setHandovers(handoverData);
          setTotalItems(res.total || 0);
          setTotalPages(res.totalPages || 1);

          // Fetch check-in history for current page's unique assets only
          const assetIds = [...new Set(handoverData.map(h => h.asset?._id || h.asset?.id).filter(Boolean))];

          // Fetch check-in dates from AssetHistory (in parallel for better performance)
          // We need to match check-ins to specific handovers
          const checkinMap = {};
          const historyPromises = handoverData.map(async (handover) => {
            const assetId = handover.asset?._id || handover.asset?.id;
            if (!assetId) return null;

            try {
              const historyRes = await api.getAssetHistory(assetId);
              if (historyRes.success && historyRes.data) {
                const checkoutDate = handover.assignedDate || handover.createdAt;
                // Find check-in that happened after this checkout date
                // This represents when admin returned the asset (not employee acknowledgment)
                const checkin = historyRes.data
                  .filter(h => h.action === 'checked_in')
                  .filter(h => new Date(h.createdAt) >= new Date(checkoutDate))
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0]; // Get earliest check-in after checkout

                if (checkin) {
                  return {
                    handoverId: handover._id || handover.id,
                    checkinDate: checkin.createdAt
                  };
                }
              }
            } catch (error) {
              console.error(`Error fetching history for asset ${assetId}:`, error);
            }
            return null;
          });

          const historyResults = await Promise.allSettled(historyPromises);
          historyResults.forEach((result) => {
            if (result.status === 'fulfilled' && result.value) {
              checkinMap[result.value.handoverId] = result.value.checkinDate;
            }
          });
          setCheckinHistory(checkinMap);
        }
      } catch (error) {
        console.error('Error loading asset history report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, itemsPerPage, filters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCheckinDate = (handover) => {
    const handoverId = handover._id || handover.id;
    // Use checkinHistory from AssetHistory (when admin checked in/returned the asset)
    // This is the actual return date, not when employee acknowledged
    if (handoverId && checkinHistory[handoverId]) {
      return checkinHistory[handoverId];
    }
    return null;
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      employee: '',
      asset: '',
      showCheckout: false,
    });
    setGroupBy('none'); // Reset Group By filter as well
  };

  const handleToggleAdmin = (adminId) => {
    setSelectedAdmins(prev => {
      if (prev.includes(adminId)) {
        return prev.filter(id => id !== adminId);
      } else {
        return [...prev, adminId];
      }
    });
  };

  const handleSelectAllAdmins = () => {
    if (selectedAdmins.length === admins.length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(admins.map(admin => admin.id || admin._id));
    }
  };

  const handleViewPhotos = (handover) => {
    const checkoutPhotos = handover.checkoutPhotos || [];
    const checkinPhotos = handover.checkinPhotos || [];
    setSelectedPhotos({ checkoutPhotos, checkinPhotos });
    setSelectedHandover(handover);
    setCurrentPhotoIndex(0);
    setShowPhotoModal(true);
  };

  const getAllPhotos = () => {
    const allPhotos = [
      ...(selectedPhotos.checkoutPhotos || []),
      ...(selectedPhotos.checkinPhotos || [])
    ];
    return allPhotos;
  };

  const handleNextPhoto = () => {
    const allPhotos = getAllPhotos();
    setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length);
  };

  const handlePrevPhoto = () => {
    const allPhotos = getAllPhotos();
    setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  };

  const handleSendEmail = async () => {
    if (selectedAdmins.length === 0) {
      toast.error('Please select at least one admin');
      return;
    }

    try {
      setSendingEmail(true);

      // Prepare filters for backend (same format as API expects)
      // IMPORTANT: This sends ALL filtered data, not just the current page
      const emailFilters = {
        assignedTo: filters.employee || undefined,
        asset: filters.asset || undefined,
        acknowledgementStatus: filters.showCheckout ? 'pending' : undefined,
        dateFrom: filters.startDate || undefined,
        dateTo: filters.endDate || undefined,
      };

      // Remove undefined values
      Object.keys(emailFilters).forEach(key =>
        emailFilters[key] === undefined && delete emailFilters[key]
      );

      const response = await api.sendEmailReport({
        adminIds: selectedAdmins,
        format: emailFormat,
        filters: emailFilters,
        groupBy: groupBy !== 'none' ? groupBy : undefined,
      });

      if (response.success) {
        toast.success(`Report sent successfully to ${response.recipients?.length || selectedAdmins.length} admin(s)`);
        setShowEmailModal(false);
      } else {
        toast.error(response.message || 'Failed to send email report');
      }
    } catch (error) {
      console.error('Error sending email report:', error);
      toast.error(error.response?.data?.message || 'Failed to send email report');
    } finally {
      setSendingEmail(false);
    }
  };

  // Group handovers based on groupBy selection
  const groupedHandovers = useMemo(() => {
    if (groupBy === 'none') {
      return null; // Return null to indicate no grouping
    }

    const grouped = {};

    handovers.forEach((handover) => {
      let groupKey;

      if (groupBy === 'employee') {
        const employeeName = handover.assignedTo?.name || 'Unknown Employee';
        groupKey = employeeName;
      } else if (groupBy === 'asset') {
        const assetTag = handover.asset?.assetTag || 'Unknown Asset';
        const assetDesc = handover.asset?.description || '';
        groupKey = `${assetTag} - ${assetDesc}`;
      }

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(handover);
    });

    // Convert to array of { groupKey, items } for easier rendering
    return Object.entries(grouped)
      .map(([groupKey, items]) => ({ groupKey, items }))
      .sort((a, b) => a.groupKey.localeCompare(b.groupKey));
  }, [handovers, groupBy]);

  if (loading && handovers.length === 0) {
    return <Loader />;
  }

  return (
    <div className="asset-history-report-page" style={{ padding: '16px' }}>
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#1a202c', margin: 0 }}>
            Asset History Report
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowEmailModal(true)}
              style={{
                padding: '8px 16px',
                background: '#667eea',
                color: 'white',
                border: '1px solid #667eea',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              📧 Email Report
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '8px 16px',
                background: showFilters ? '#667eea' : '#f7fafc',
                color: showFilters ? 'white' : '#4a5568',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div
            style={{
              background: '#f7fafc',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#2d3748', margin: 0 }}>
                Filters
              </h3>
              <button
                onClick={handleResetFilters}
                style={{
                  padding: '6px 12px',
                  background: 'white',
                  color: '#667eea',
                  border: '1px solid #667eea',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Reset Filters
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {/* Start Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: 'white'
                  }}
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: 'white'
                  }}
                />
              </div>

              {/* Employee */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
                  Employee
                </label>
                <select
                  value={filters.employee}
                  onChange={(e) => handleFilterChange('employee', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: 'white'
                  }}
                >
                  <option value="">All Employees</option>
                  {employees.map(emp => (
                    <option key={emp.id || emp._id} value={emp.id || emp._id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Asset */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
                  Asset
                </label>
                <select
                  value={filters.asset}
                  onChange={(e) => handleFilterChange('asset', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: 'white'
                  }}
                >
                  <option value="">All Assets</option>
                  {assets.map(asset => {
                    const description = asset.description || 'N/A';
                    const maxLength = 30;
                    const truncatedDesc = description.length > maxLength
                      ? description.substring(0, maxLength) + '...'
                      : description;
                    return (
                      <option key={asset.id || asset._id} value={asset.id || asset._id} title={`${asset.assetTag} - ${description}`}>
                        {asset.assetTag} - {truncatedDesc}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Checkbox and Group By Row */}
            <div style={{ display: 'flex', gap: '24px', marginTop: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4a5568', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filters.showCheckout}
                  onChange={(e) => handleFilterChange('showCheckout', e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span>Show Checkout Only (Check In Pending)</span>
              </label>

              {/* Group By */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568' }}>
                  Group By:
                </label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="none">None</option>
                  <option value="employee">Employee</option>
                  <option value="asset">Asset</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {handovers.length === 0 ? (
          <p style={{ fontSize: '13px', color: '#a0aec0', margin: 0 }}>
            No asset history available
          </p>
        ) : (
          <>
            <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px',
                  minWidth: '1500px',
                }}
              >
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f7fafc' }}>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Asset Photo
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Asset Tag ID
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Description
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Check-out Date
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Check-in Date
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Cost
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Brand
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Model
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Serial No
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Site
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Location
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Assigned To
                    </th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', color: '#4a5568', fontSize: '12px', fontWeight: 600 }}>
                      Photos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupedHandovers ? (
                    // Render grouped data
                    groupedHandovers.map((group) => (
                      <React.Fragment key={group.groupKey}>
                        {/* Group Header Row */}
                        <tr style={{ background: '#edf2f7', borderTop: '2px solid #cbd5e0' }}>
                          <td
                            colSpan={13}
                            style={{
                              padding: '12px 16px',
                              fontWeight: 600,
                              fontSize: '14px',
                              color: '#2d3748',
                            }}
                          >
                            {groupBy === 'employee' ? '👤 ' : '📦 '}
                            {group.groupKey} ({group.items.length} {group.items.length === 1 ? 'record' : 'records'})
                          </td>
                        </tr>
                        {/* Group Items */}
                        {group.items.map((h) => {
                          const asset = h.asset || {};
                          const firstPhoto = asset.photos && asset.photos.length > 0
                            ? (asset.photos[0].url || asset.photos[0])
                            : null;

                          return (
                            <tr
                              key={h._id || h.id}
                              style={{
                                borderBottom: '1px solid #f1f5f9',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {firstPhoto ? (
                                  <img
                                    src={firstPhoto}
                                    alt={asset.assetTag || 'Asset'}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #e2e8f0',
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                ) : null}
                                <div
                                  style={{
                                    display: firstPhoto ? 'none' : 'flex',
                                    width: '50px',
                                    height: '50px',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#e2e8f0',
                                    borderRadius: '4px',
                                    color: '#718096',
                                    fontSize: '20px',
                                  }}
                                >
                                  📦
                                </div>
                              </td>
                              <td style={{ padding: '10px 8px', color: '#2d3748', fontWeight: 500 }}>
                                {asset.assetTag || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568', maxWidth: '200px' }}>
                                <div style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {asset.description || 'N/A'}
                                </div>
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {formatDate(h.assignedDate || h.createdAt)}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {formatDate(getCheckinDate(h))}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {formatCurrency(asset.cost)}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {asset.brand || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {asset.model || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {asset.serialNumber || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {asset.location?.site?.name || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {asset.location?.locationDetail || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#2d3748', fontWeight: 500 }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span>{h.assignedTo?.name || 'N/A'}</span>
                                  {h.handoverType === 'transfer' && h.assignedBy?.name && (
                                    <span style={{
                                      fontSize: '11px',
                                      color: '#718096',
                                      fontStyle: 'italic',
                                      marginTop: '2px'
                                    }}>
                                      Transferred by: {h.assignedBy.name}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                                {((h.checkoutPhotos && h.checkoutPhotos.length > 0) || (h.checkinPhotos && h.checkinPhotos.length > 0)) ? (
                                  <button
                                    onClick={() => handleViewPhotos(h)}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#667eea',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    📷 Photos
                                  </button>
                                ) : (
                                  <span style={{ color: '#a0aec0', fontSize: '12px' }}>No photos</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  ) : (
                    // Render ungrouped data (normal view)
                    handovers.map((h) => {
                      const asset = h.asset || {};
                      const firstPhoto = asset.photos && asset.photos.length > 0
                        ? (asset.photos[0].url || asset.photos[0])
                        : null;

                      return (
                        <tr
                          key={h._id || h.id}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {firstPhoto ? (
                              <img
                                src={firstPhoto}
                                alt={asset.assetTag || 'Asset'}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #e2e8f0',
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                display: firstPhoto ? 'none' : 'flex',
                                width: '50px',
                                height: '50px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#e2e8f0',
                                borderRadius: '4px',
                                color: '#718096',
                                fontSize: '20px',
                              }}
                            >
                              📦
                            </div>
                          </td>
                          <td style={{ padding: '10px 8px', color: '#2d3748', fontWeight: 500 }}>
                            {asset.assetTag || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568', maxWidth: '200px' }}>
                            <div style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {asset.description || 'N/A'}
                            </div>
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {formatDate(h.assignedDate || h.createdAt)}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {formatDate(getCheckinDate(h))}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {formatCurrency(asset.cost)}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {asset.brand || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {asset.model || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {asset.serialNumber || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {asset.location?.site?.name || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {asset.location?.locationDetail || 'N/A'}
                          </td>
                          <td style={{ padding: '10px 8px', color: '#2d3748', fontWeight: 500 }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span>{h.assignedTo?.name || 'N/A'}</span>
                              {h.handoverType === 'transfer' && h.assignedBy?.name && (
                                <span style={{
                                  fontSize: '11px',
                                  color: '#718096',
                                  fontStyle: 'italic',
                                  marginTop: '2px'
                                }}>
                                  Transferred by: {h.assignedBy.name}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '10px 8px', color: '#4a5568' }}>
                            {((h.checkoutPhotos && h.checkoutPhotos.length > 0) || (h.checkinPhotos && h.checkinPhotos.length > 0)) ? (
                              <button
                                onClick={() => handleViewPhotos(h)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#667eea',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                📷 Photos
                              </button>
                            ) : (
                              <span style={{ color: '#a0aec0', fontSize: '12px' }}>No photos</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newItemsPerPage) => {
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1);
              }}
              showItemsPerPage={true}
            />
          </>
        )}
      </div>

      {/* Email Report Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !sendingEmail && setShowEmailModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1a202c', margin: 0 }}>
                Email Report
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Format Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#4a5568' }}>
                Format
              </label>
              <select
                value={emailFormat}
                onChange={(e) => setEmailFormat(e.target.value)}
                disabled={sendingEmail}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: sendingEmail ? 'not-allowed' : 'pointer',
                }}
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel (XLSX)</option>
              </select>
            </div>

            {/* Admin Selection */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#4a5568' }}>
                  Select Admins
                </label>
                <button
                  onClick={handleSelectAllAdmins}
                  disabled={sendingEmail}
                  style={{
                    padding: '4px 12px',
                    background: 'transparent',
                    color: '#667eea',
                    border: '1px solid #667eea',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: sendingEmail ? 'not-allowed' : 'pointer',
                  }}
                >
                  {selectedAdmins.length === admins.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div
                style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '8px',
                }}
              >
                {admins.length === 0 ? (
                  <p style={{ padding: '8px', color: '#a0aec0', fontSize: '13px', margin: 0 }}>
                    No admins found
                  </p>
                ) : (
                  admins.map((admin) => {
                    const adminId = admin.id || admin._id;
                    const isSelected = selectedAdmins.includes(adminId);
                    return (
                      <label
                        key={adminId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px',
                          cursor: sendingEmail ? 'not-allowed' : 'pointer',
                          borderRadius: '4px',
                          backgroundColor: isSelected ? '#f7fafc' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleAdmin(adminId)}
                          disabled={sendingEmail}
                          style={{ width: '18px', height: '18px', cursor: sendingEmail ? 'not-allowed' : 'pointer' }}
                        />
                        <span style={{ fontSize: '13px', color: '#4a5568' }}>
                          {admin.name} ({admin.email})
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px', margin: '8px 0 0 0' }}>
                {selectedAdmins.length} of {admins.length} admin(s) selected
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
                style={{
                  padding: '10px 20px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: sendingEmail ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || selectedAdmins.length === 0}
                style={{
                  padding: '10px 20px',
                  background: sendingEmail || selectedAdmins.length === 0 ? '#cbd5e0' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: sendingEmail || selectedAdmins.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {sendingEmail ? (
                  <>
                    <span>⏳</span> Sending...
                  </>
                ) : (
                  <>
                    <span>📧</span> Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {showPhotoModal && (() => {
        const allPhotos = getAllPhotos();
        const currentPhoto = allPhotos[currentPhotoIndex];

        if (allPhotos.length === 0) {
          return (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
              onClick={() => setShowPhotoModal(false)}
            >
              <div style={{ textAlign: 'center', color: 'white', fontSize: '18px' }}>
                No photos available
              </div>
            </div>
          );
        }

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setShowPhotoModal(false)}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPhotoModal(false)}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#2d3748',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1001,
                  fontWeight: 'bold',
                }}
              >
                ×
              </button>

              {/* Previous Button */}
              {allPhotos.length > 1 && (
                <button
                  onClick={handlePrevPhoto}
                  style={{
                    position: 'absolute',
                    left: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#2d3748',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1001,
                    fontWeight: 'bold',
                  }}
                >
                  ‹
                </button>
              )}

              {/* Image */}
              <img
                src={currentPhoto?.url || currentPhoto}
                alt={`Photo ${currentPhotoIndex + 1}`}
                style={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />

              {/* Next Button */}
              {allPhotos.length > 1 && (
                <button
                  onClick={handleNextPhoto}
                  style={{
                    position: 'absolute',
                    right: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#2d3748',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1001,
                    fontWeight: 'bold',
                  }}
                >
                  ›
                </button>
              )}

              {/* Photo Counter */}
              {allPhotos.length > 1 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  {currentPhotoIndex + 1} / {allPhotos.length}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AssetHistoryReportPage;
